import React, { useRef, useEffect, useState, useCallback } from 'react'
import { evaluateGameStats, subscribeAchievementUnlocked } from './achievements'
import { playOrbSound, playTrophySound } from './audio'

export function CadenceRunner({ polar, demo, hrv, onExit }) {
  const canvasRef = useRef(null)

  // Estados de React
  const [gameState, setGameState] = useState('menu') // 'menu' | 'playing' | 'gameover'
  const [score, setScore] = useState(0)
  const [targetCadence, setTargetCadence] = useState(155) // Pasos por minuto objetivo
  const [spm, setSpm] = useState(0) // Cadencia actual
  const [reactorPct, setReactorPct] = useState(0) // Carga del reactor de sobremarcha
  const [distanceKm, setDistanceKm] = useState('0.00')
  const [unlockedToast, setUnlockedToast] = useState(null)
  const [lastStats, setLastStats] = useState(null)

  // Referencias para el bucle 60 FPS
  const stateRef = useRef({
    distance: 0,
    score: 0,
    reactorPct: 0,
    currentSpm: 0,
    currentHr: 120,
    targetCadence: 155,
    inZone2: true,
    overdriveTime: 0,
    ringsCollected: 0,
    rings: [],
    particles: [],
    speed: 10,
    startTime: 0,
    lastStepTs: 0,
    stepIntervals: [],
  })

  // Pulso real o simulado
  const liveHr = (polar && polar.status === 'connected' && polar.hr)
    ? polar.hr
    : (demo && demo.active && demo.hr)
      ? demo.hr
      : 124

  const isBleConnected = (polar && polar.status === 'connected') || (demo && demo.active)

  // Cálculo de cadencia en vivo (SPM) a partir de los pasos del Polar H10
  const stepsCount = (polar && polar.steps) || (demo && demo.steps) || 0
  const prevStepsRef = useRef(stepsCount)
  const lastStepTimeRef = useRef(Date.now())

  useEffect(() => {
    const now = Date.now()
    const diff = stepsCount - prevStepsRef.current
    if (diff > 0) {
      const dtMin = (now - lastStepTimeRef.current) / 60000
      if (dtMin > 0.003 && dtMin < 0.1) {
        const instantSpm = Math.round(diff / dtMin)
        if (instantSpm >= 60 && instantSpm <= 220) {
          stateRef.current.currentSpm = instantSpm
          setSpm(instantSpm)
        }
      }
      prevStepsRef.current = stepsCount
      lastStepTimeRef.current = now
    }
  }, [stepsCount])

  // Actualizar referencias biomédicas
  useEffect(() => {
    const s = stateRef.current
    s.currentHr = liveHr
    s.targetCadence = targetCadence
    // Zona 2 aeróbica estándar (115 a 138 BPM)
    s.inZone2 = liveHr >= 110 && liveHr <= 140
  }, [liveHr, targetCadence])

  // Suscripción a logros
  useEffect(() => {
    const unsub = subscribeAchievementUnlocked(ach => {
      playTrophySound()
      setUnlockedToast(ach)
      setTimeout(() => setUnlockedToast(null), 3600)
    })
    return unsub
  }, [])

  // Iniciar carrera
  const startRun = useCallback(() => {
    stateRef.current = {
      distance: 0,
      score: 0,
      reactorPct: 0,
      currentSpm: 155,
      currentHr: liveHr,
      targetCadence,
      inZone2: true,
      overdriveTime: 0,
      ringsCollected: 0,
      rings: [],
      particles: [],
      speed: 12,
      startTime: Date.now(),
      lastStepTs: 0,
      stepIntervals: [],
    }
    setScore(0)
    setReactorPct(0)
    setDistanceKm('0.00')
    setGameState('playing')
  }, [liveHr, targetCadence])

  // Soporte de paso manual por teclado (Espacio / Clic) para pruebas
  const triggerManualStep = () => {
    const s = stateRef.current
    const now = Date.now()
    if (s.lastStepTs > 0) {
      const deltaSec = (now - s.lastStepTs) / 1000
      if (deltaSec > 0.25 && deltaSec < 1.2) {
        const calcSpm = Math.round(60 / deltaSec)
        s.currentSpm = calcSpm
        setSpm(calcSpm)
      }
    }
    s.lastStepTs = now
  }

  // ── Bucle Canvas 3D a 60 FPS ──
  useEffect(() => {
    if (gameState !== 'playing') return

    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    let animId = null
    let lastTime = performance.now()
    let spawnTimer = 0

    const loop = (now) => {
      const dt = Math.min(50, now - lastTime) / 1000
      lastTime = now

      const s = stateRef.current
      const width = canvas.width
      const height = canvas.height

      // Evaluación del reactor: sincronía de cadencia y Zona 2
      const spmDiff = Math.abs(s.currentSpm - s.targetCadence)
      const isSpmLocked = spmDiff <= 8 // Dentro de +-8 pasos/min del metrónomo

      if (isSpmLocked && s.inZone2) {
        // En sintonía perfecta: carga el reactor
        s.reactorPct = Math.min(100, s.reactorPct + 24 * dt)
      } else {
        // Desfasado: se descarga suavemente
        s.reactorPct = Math.max(0, s.reactorPct - 14 * dt)
      }
      setReactorPct(Math.round(s.reactorPct))

      const isOverdrive = s.reactorPct >= 95
      if (isOverdrive) s.overdriveTime += dt
      const mult = isOverdrive ? 4 : (s.reactorPct >= 50 ? 2 : 1)

      // Avance de velocidad y distancia
      s.speed = 10 + (s.reactorPct / 100) * 8
      s.distance += s.speed * dt * 2
      s.score += Math.round(s.speed * dt * mult * 3)
      setScore(s.score)
      setDistanceKm((s.distance / 1000).toFixed(2))

      // Generación de anillos de energía
      spawnTimer += dt
      if (spawnTimer >= 0.8) {
        spawnTimer = 0
        s.rings.push({ z: 340, xRatio: (Math.random() - 0.5) * 0.8 })
      }

      // Actualizar anillos
      for (let i = s.rings.length - 1; i >= 0; i--) {
        const r = s.rings[i]
        r.z -= s.speed * 18 * dt

        // Recolección
        if (r.z <= 35 && r.z >= 10) {
          playOrbSound()
          s.ringsCollected += 1
          s.score += 50 * mult
          s.rings.splice(i, 1)

          // Partículas
          for (let p = 0; p < 8; p++) {
            s.particles.push({
              x: width * 0.5 + (Math.random() - 0.5) * 60,
              y: height * 0.75,
              vx: (Math.random() - 0.5) * 160,
              vy: (Math.random() - 0.5) * 160,
              color: isOverdrive ? '#64d2ff' : '#30d158',
              life: 0.35
            })
          }
          continue
        }

        if (r.z <= 0) s.rings.splice(i, 1)
      }

      // Actualizar partículas
      for (let i = s.particles.length - 1; i >= 0; i--) {
        const p = s.particles[i]
        p.x += p.vx * dt
        p.y += p.vy * dt
        p.life -= dt
        if (p.life <= 0) s.particles.splice(i, 1)
      }

      // ── RENDERIZADO VISUAL ──
      ctx.clearRect(0, 0, width, height)

      const horizonY = height * 0.34
      const groundY = height * 0.9

      // Cielo y Ciudad Cibernética
      const sky = ctx.createLinearGradient(0, 0, 0, horizonY)
      sky.addColorStop(0, '#04060c')
      sky.addColorStop(1, isOverdrive ? '#0f2648' : '#0a101f')
      ctx.fillStyle = sky
      ctx.fillRect(0, 0, width, horizonY)

      // Suelo / Pista
      const road = ctx.createLinearGradient(0, horizonY, 0, height)
      road.addColorStop(0, '#0c1628')
      road.addColorStop(1, '#05070e')
      ctx.fillStyle = road
      ctx.fillRect(0, horizonY, width, height - horizonY)

      // Líneas de pista con efecto de velocidad
      const lineOffset = (s.distance * 16) % 55
      ctx.strokeStyle = isOverdrive ? '#64d2ff' : s.inZone2 ? '#30d158' : '#ff9f0a'
      ctx.lineWidth = isOverdrive ? 2 : 1.2

      for (let y = horizonY + lineOffset; y < groundY; y += 45) {
        const p = (y - horizonY) / (groundY - horizonY)
        const spread = width * 0.45 * p
        ctx.beginPath()
        ctx.moveTo(width * 0.5 - spread, y)
        ctx.lineTo(width * 0.5 + spread, y)
        ctx.stroke()
      }

      // Dibujar Anillos de Cadencia
      s.rings.forEach(r => {
        const p = Math.max(0.05, Math.min(1, 1 - (r.z / 340)))
        const curY = horizonY + (groundY - horizonY) * p
        const spread = width * 0.45 * p
        const curX = width * 0.5 + r.xRatio * spread
        const scale = p * 1.8

        ctx.beginPath()
        ctx.arc(curX, curY - 20 * scale, 16 * scale, 0, Math.PI * 2)
        ctx.strokeStyle = isOverdrive ? '#64d2ff' : '#30d158'
        ctx.shadowColor = isOverdrive ? '#64d2ff' : '#30d158'
        ctx.shadowBlur = 12 * scale
        ctx.lineWidth = 3 * scale
        ctx.stroke()
        ctx.shadowBlur = 0
      })

      // Dibujar Corredor / Avatar en el centro
      const runnerX = width * 0.5
      const runnerY = groundY - 28

      // Aura de SobreMarcha
      if (isOverdrive) {
        ctx.beginPath()
        ctx.arc(runnerX, runnerY, 32, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(100,210,255,0.22)'
        ctx.shadowColor = '#64d2ff'
        ctx.shadowBlur = 24
        ctx.fill()
        ctx.shadowBlur = 0
      }

      ctx.beginPath()
      ctx.arc(runnerX, runnerY, 16, 0, Math.PI * 2)
      ctx.fillStyle = isOverdrive ? '#64d2ff' : s.inZone2 ? '#30d158' : '#ff9f0a'
      ctx.fill()
      ctx.strokeStyle = '#ffffff'
      ctx.lineWidth = 2
      ctx.stroke()

      // Dibujar Partículas
      s.particles.forEach(p => {
        ctx.beginPath()
        ctx.arc(p.x, p.y, 3, 0, Math.PI * 2)
        ctx.fillStyle = p.color
        ctx.fill()
      })

      animId = requestAnimationFrame(loop)
    }

    animId = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(animId)
  }, [gameState, isBleConnected])

  return (
    <div style={{
      position: 'relative', width: '100%', maxWidth: 840, margin: '0 auto',
      background: '#07090e', borderRadius: 18, border: '1px solid rgba(255,255,255,0.12)',
      overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'none'
    }}>
      {/* ── 1. HUD Superior en Vivo ── */}
      <div style={{
        padding: '10px 16px', background: 'rgba(255,255,255,0.04)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 10
      }}>
        {/* Cadencia y Metrónomo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 900, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
            {spm} <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>SPM</span>
          </span>
          <span style={{
            fontSize: 9.5, fontWeight: 800, padding: '2px 7px', borderRadius: 6,
            background: Math.abs(spm - targetCadence) <= 8 ? 'rgba(48,209,88,0.22)' : 'rgba(255,159,10,0.22)',
            color: Math.abs(spm - targetCadence) <= 8 ? '#30d158' : '#ff9f0a'
          }}>
            META: {targetCadence} SPM
          </span>
        </div>

        {/* Barra de Carga del Reactor (Overdrive) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 160 }}>
          <span style={{ fontSize: 9.5, fontWeight: 800, color: reactorPct >= 95 ? '#64d2ff' : 'rgba(255,255,255,0.6)', textTransform: 'uppercase' }}>
            {reactorPct >= 95 ? '⚡ SOBREMARCHA x4' : `REACTOR ${reactorPct}%`}
          </span>
          <div style={{ height: 6, flex: 1, background: 'rgba(255,255,255,0.15)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              width: `${reactorPct}%`, height: '100%',
              background: reactorPct >= 95 ? 'linear-gradient(90deg, #30d158, #64d2ff)' : '#30d158',
              transition: 'width 0.15s ease'
            }} />
          </div>
        </div>

        {/* Distancia y Salir */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 15, fontWeight: 900, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
            {distanceKm} <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>KM</span>
          </span>
          <span style={{ fontSize: 15, fontWeight: 900, color: '#ffd700', fontVariantNumeric: 'tabular-nums' }}>
            {score} <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>PTS</span>
          </span>
          <button
            onClick={onExit}
            style={{
              background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 6,
              color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: 700,
              padding: '4px 10px', cursor: 'pointer', fontFamily: 'inherit'
            }}
          >
            ✕ Salir
          </button>
        </div>
      </div>

      {/* ── 2. Canvas 3D de la Pista ── */}
      <div
        onClick={triggerManualStep}
        style={{ position: 'relative', width: '100%', height: 420, cursor: 'pointer' }}
      >
        <canvas
          ref={canvasRef}
          width={840}
          height={420}
          style={{ width: '100%', height: '100%', display: 'block' }}
        />

        {/* Notificación Toast de Trofeo */}
        {unlockedToast && (
          <div style={{
            position: 'absolute', top: 16, right: 16,
            background: 'rgba(15, 20, 32, 0.92)', border: '1.5px solid #ffd700',
            borderRadius: 12, padding: '8px 14px', boxShadow: '0 8px 28px rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', gap: 10,
            animation: 'fadeIn 0.25s ease', backdropFilter: 'blur(12px)', zIndex: 30
          }}>
            <span style={{ fontSize: 16 }}>★</span>
            <div>
              <span style={{ fontSize: 9.5, fontWeight: 800, color: '#ffd700', textTransform: 'uppercase', letterSpacing: 0.6, display: 'block' }}>
                ¡Trofeo Desbloqueado!
              </span>
              <span style={{ fontSize: 12, fontWeight: 900, color: '#fff' }}>
                {unlockedToast.title}
              </span>
            </div>
          </div>
        )}

        {/* ── Menú de Inicio ── */}
        {gameState === 'menu' && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(circle, rgba(15,20,32,0.95) 0%, rgba(6,8,14,0.98) 100%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 24, textAlign: 'center', zIndex: 20
          }}>
            <h2 style={{ fontSize: 26, fontWeight: 900, color: '#fff', margin: '0 0 8px', letterSpacing: -0.5 }}>
              Carrera de Cadencia (Zona 2)
            </h2>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', maxWidth: 460, margin: '0 0 16px', lineHeight: 1.5 }}>
              Para caminar, trotar en la cinta o correr al aire libre con el Polar H10:
              <br />
              Sincronizá tus pasos al metrónomo (<strong style={{ color: '#30d158' }}>{targetCadence} SPM</strong>) y mantené tu pulso en <strong style={{ color: '#007aff' }}>Zona 2 aeróbica</strong> para cargar el Reactor al 100% y activar la <strong>Sobremarcha x4</strong>.
            </p>

            {/* Selector de Cadencia Objetivo */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
              {[145, 155, 165, 175].map(val => (
                <button
                  key={val}
                  onClick={() => setTargetCadence(val)}
                  style={{
                    background: targetCadence === val ? '#007aff' : 'rgba(255,255,255,0.08)',
                    color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px',
                    fontSize: 12, fontWeight: 700, cursor: 'pointer'
                  }}
                >
                  {val} SPM
                </button>
              ))}
            </div>

            <button
              onClick={startRun}
              style={{
                background: '#30d158', color: '#000', border: 'none',
                borderRadius: 12, padding: '12px 30px', fontSize: 14, fontWeight: 900,
                cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 18px rgba(48,209,88,0.4)'
              }}
            >
              ¡ARRANCAR CARRERA!
            </button>
          </div>
        )}

        {/* ── Game Over / Resultados ── */}
        {gameState === 'gameover' && lastStats && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(circle, rgba(15,20,32,0.95) 0%, rgba(6,8,14,0.98) 100%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 24, textAlign: 'center', zIndex: 20
          }}>
            <h2 style={{ fontSize: 26, fontWeight: 900, color: '#fff', margin: '0 0 10px' }}>
              Entrenamiento de Cadencia Finalizado
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, width: '100%', maxWidth: 380, marginBottom: 18 }}>
              <div style={{ background: 'rgba(255,255,255,0.06)', padding: '8px', borderRadius: 8 }}>
                <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.5)', display: 'block' }}>Distancia</span>
                <span style={{ fontSize: 18, fontWeight: 900, color: '#30d158' }}>{distanceKm} km</span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.06)', padding: '8px', borderRadius: 8 }}>
                <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.5)', display: 'block' }}>Puntos</span>
                <span style={{ fontSize: 18, fontWeight: 900, color: '#ffd700' }}>{score}</span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.06)', padding: '8px', borderRadius: 8 }}>
                <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.5)', display: 'block' }}>Sobremarcha</span>
                <span style={{ fontSize: 18, fontWeight: 900, color: '#64d2ff' }}>{Math.round(stateRef.current.overdriveTime)}s</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={startRun}
                style={{
                  background: '#30d158', color: '#000', border: 'none',
                  borderRadius: 10, padding: '10px 22px', fontSize: 13, fontWeight: 800,
                  cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                Correr de Nuevo
              </button>
              <button
                onClick={() => setGameState('menu')}
                style={{
                  background: 'rgba(255,255,255,0.12)', color: '#fff', border: 'none',
                  borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                Menú
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── 3. Instrucción al pie ── */}
      <div style={{
        padding: '8px 16px', background: 'rgba(255,255,255,0.02)',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: 'rgba(255,255,255,0.5)'
      }}>
        <span>Acelerómetro Polar H10: Cadencia de pasos en vivo (SPM) y Zona 2 aeróbica</span>
        <span>Modo escritorio: Clic o barra de espacio para marcar pasos manualmente</span>
      </div>
    </div>
  )
}
